// src/pages/vendor/VendorSubscription.jsx
import React, { useEffect, useMemo, useState } from "react";
import { auth, db, functions } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const PRO_AMOUNT_XOF = 15000;

const PLANS = [
  {
    id: "standard",
    name: "Pack Standard",
    price: "0 FCFA / mois",
    subtitle: "Commission sur chaque vente",
    badge: "Idéal pour démarrer",
    description:
      "Aucun abonnement mensuel. Vous payez uniquement une commission par vente validée.",
    billingMode: "commission",
    mainPoints: [
      "Pas de frais mensuels",
      "Paiement Mobile Money & à la livraison",
      "Gestion simple de vos produits",
      "Support de base par email / WhatsApp",
    ],
  },
  {
    id: "pro",
    name: "Pack Professionnel",
    price: "15 000 FCFA / mois*",
    subtitle: "Abonnement mensuel + outils avancés",
    badge: "Pour boutiques en croissance",
    description:
      "Accédez à tous les outils avancés GoShop pour développer votre boutique plus rapidement.",
    billingMode: "monthly",
    mainPoints: [
      "Outils d’automatisation & statistiques",
      "Mise en avant régulière sur le site",
      "Accès prioritaire au support",
      "Préparation B2B & vente multi-villes",
    ],
  },
];

export default function VendorSubscription() {
  const [userDoc, setUserDoc] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const fbUser = auth.currentUser;
  const vendorId = fbUser?.uid || null;

  const [payNetwork, setPayNetwork] = useState("TMONEY");
  const [payPhone, setPayPhone] = useState("");

  // Charger le document user
  useEffect(() => {
    const fetchUser = async () => {
      if (!vendorId) {
        setLoadingUser(false);
        return;
      }
      try {
        setLoadingUser(true);
        const ref = doc(db, "users", vendorId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setUserDoc({ id: snap.id, ...snap.data() });
        } else {
          setUserDoc(null);
        }
      } catch (err) {
        console.error("Erreur chargement user:", err);
        setError("Impossible de charger vos informations vendeur.");
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, [vendorId]);

  const currentPlanId = useMemo(() => {
    const nested = userDoc?.subscription?.planId || null;
    const fallback = userDoc?.subscriptionPlan || null;
    return nested || fallback || null;
  }, [userDoc]);

  const currentStatus = useMemo(() => {
    const nested = userDoc?.subscription?.status || null;
    const fallback = userDoc?.subscriptionStatus || null;
    return nested || fallback || null;
  }, [userDoc]);

  const currentLabel = useMemo(() => {
    const nested = userDoc?.subscription?.planLabel || null;
    return nested || currentPlanId || "—";
  }, [userDoc, currentPlanId]);

  const getStatusLabel = () => {
    if (!currentPlanId) return "Aucun abonnement actif";
    if (currentStatus === "active") return `Abonnement actif : ${currentLabel}`;
    if (currentStatus === "pending_payment")
      return `En attente de paiement pour le ${currentLabel}`;
    if (currentStatus === "inactive")
      return `Abonnement inactif : ${currentLabel}`;
    return `Abonnement : ${currentLabel}`;
  };

  // ✅ Standard : activation immédiate
  const activateStandard = async () => {
    if (!vendorId) {
      setError("Vous devez être connecté en tant que vendeur.");
      return;
    }

    setError("");
    setInfo("");
    setSavingPlanId("standard");

    try {
      const userRef = doc(db, "users", vendorId);

      const sub = {
        planId: "standard",
        planLabel: "Pack Standard",
        billingMode: "commission",
        status: "active",
        startedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(userRef, {
        subscription: sub,
        isVendor: true,
        subscriptionPlan: "standard",
        subscriptionStatus: "active",
        subscriptionValidUntil: null,
      });

      setUserDoc((prev) =>
        prev
          ? {
              ...prev,
              subscription: sub,
              isVendor: true,
              subscriptionPlan: "standard",
              subscriptionStatus: "active",
              subscriptionValidUntil: null,
            }
          : {
              subscription: sub,
              isVendor: true,
              subscriptionPlan: "standard",
              subscriptionStatus: "active",
              subscriptionValidUntil: null,
            }
      );

      setInfo(
        "Le Pack Standard est maintenant actif pour votre boutique. Vous payez une commission sur chaque vente."
      );
    } catch (err) {
      console.error(err);
      setError(
        "Une erreur est survenue lors de l’activation du Pack Standard."
      );
    } finally {
      setSavingPlanId(null);
    }
  };

  // ✅ Pro : paiement PayGate → redirection (token côté serveur)
  const startProPayment = async () => {
    if (!vendorId) {
      setError("Vous devez être connecté.");
      return;
    }

    setError("");
    setInfo("");
    setSavingPlanId("pro");

    try {
      const cleanPhone = String(payPhone || "").replace(/\D/g, ""); // enlève + espaces etc.

      // 1) passe en pending (pour l’UI)
      const userRef = doc(db, "users", vendorId);
      await updateDoc(userRef, {
        isVendor: true,
        subscriptionPlan: "pro",
        subscriptionStatus: "pending_payment",
        subscription: {
          planId: "pro",
          planLabel: "Pack Professionnel",
          billingMode: "monthly",
          status: "pending_payment",
          updatedAt: serverTimestamp(),
          startedAt: null,
        },
      });

      // 2) demande au backend une URL paygate (montant fixé côté serveur)
      const fn = httpsCallable(functions, "createProSubscriptionPaygateUrl");
      const origin = window.location.origin;

      const res = await fn({
        origin,
        returnUrl: `${origin}/payment-return-subscription`,
        phone: cleanPhone || null,
        network: payNetwork || "TMONEY",
      });

      const { payUrl, amountExpected } = res.data || {};
      if (!payUrl) throw new Error("URL PayGate introuvable (backend).");

      // sécurité: on affiche le montant attendu (du backend)
      const a = Number(amountExpected || PRO_AMOUNT_XOF);
      setInfo(
        `Redirection PayGate… Montant attendu : ${a.toLocaleString()} FCFA`
      );

      window.location.href = payUrl;
    } catch (err) {
      console.error(err);
      setError(err?.message || "Impossible de démarrer le paiement.");
    } finally {
      setSavingPlanId(null);
    }
  };

  // Non connecté
  if (!fbUser) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f3f4f6",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "24px 16px",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: 16,
            padding: "18px 18px 16px",
            border: "1px solid #e5e7eb",
            maxWidth: 420,
            textAlign: "center",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            Connexion requise
          </h2>
          <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 0 }}>
            Vous devez être connecté pour accéder à votre abonnement vendeur.
            Veuillez vous connecter via la page <strong>/login</strong>.
          </p>
        </div>
      </div>
    );
  }

  const isPro = currentPlanId === "pro";
  const isStandard = currentPlanId === "standard";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f4f6",
        display: "flex",
        justifyContent: "center",
        padding: "24px 16px 40px",
      }}
    >
      <main style={{ width: "100%", maxWidth: "1120px", fontSize: 14 }}>
        <header style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            Mon abonnement vendeur
          </h1>
          <p style={{ fontSize: 13, color: "#4b5563", marginTop: 6 }}>
            Gérez le pack GoShop associé à votre boutique.
          </p>

          <div
            style={{
              fontSize: 12,
              color: "#111827",
              background: "#e0f2fe",
              borderRadius: 999,
              padding: "4px 10px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: loadingUser
                  ? "#9ca3af"
                  : !currentPlanId
                  ? "#9ca3af"
                  : currentStatus === "active"
                  ? "#22c55e"
                  : currentStatus === "pending_payment"
                  ? "#facc15"
                  : "#9ca3af",
              }}
            />
            {loadingUser ? "Chargement…" : getStatusLabel()}
          </div>
        </header>

        {error && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 10px",
              borderRadius: 10,
              background: "#fee2e2",
              color: "#b91c1c",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {info && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 10px",
              borderRadius: 10,
              background: "#ecfdf5",
              color: "#166534",
              fontSize: 13,
            }}
          >
            {info}
          </div>
        )}

        <section>
          <div className="vs-plans-grid">
            {PLANS.map((plan) => {
              const isCurrent = currentPlanId === plan.id;
              const isActive = isCurrent && currentStatus === "active";
              const isPending = isCurrent && currentStatus === "pending_payment";

              return (
                <article
                  key={plan.id}
                  className={
                    "vs-plan-card" + (isActive ? " vs-plan-card-active" : "")
                  }
                >
                  <header className="vs-plan-header">
                    <div>
                      <h2 className="vs-plan-title">{plan.name}</h2>
                      <p className="vs-plan-price">{plan.price}</p>
                      <p className="vs-plan-subtitle">{plan.subtitle}</p>
                    </div>
                    <span className="vs-plan-badge">{plan.badge}</span>
                  </header>

                  <p className="vs-plan-description">{plan.description}</p>

                  <ul className="vs-plan-list">
                    {plan.mainPoints.map((pt) => (
                      <li key={pt}>
                        <span className="vs-plan-check">✓</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="vs-plan-footer">
                    {plan.id === "standard" && (
                      <>
                        <button
                          type="button"
                          className={
                            "vs-btn " + (isActive ? "vs-btn-outline" : "vs-btn-light")
                          }
                          onClick={activateStandard}
                          disabled={savingPlanId === "standard" || loadingUser || isActive}
                        >
                          {isActive
                            ? "Abonnement actuel"
                            : savingPlanId === "standard"
                            ? "Activation…"
                            : "Choisir le Pack Standard"}
                        </button>
                      </>
                    )}

                    {plan.id === "pro" && (
                      <>
                        {isActive && (
                          <button className="vs-btn vs-btn-outline" disabled>
                            Abonnement actuel
                          </button>
                        )}

                        {isPending && (
                          <button className="vs-btn vs-btn-outline vs-btn-warning" disabled>
                            En attente de paiement…
                          </button>
                        )}

                        {!isActive && (
                          <div style={{ display: "grid", gap: 8 }}>
                            <div className="row g-2">
                              <div className="col-12">
                                <label className="form-label small mb-1">
                                  Téléphone Mobile Money (optionnel)
                                </label>
                                <input
                                  className="form-control form-control-sm"
                                  value={payPhone}
                                  onChange={(e) => setPayPhone(e.target.value)}
                                  placeholder="Ex: 22890000000"
                                  disabled={savingPlanId === "pro" || loadingUser}
                                />
                              </div>
                              <div className="col-12">
                                <label className="form-label small mb-1">Réseau</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={payNetwork}
                                  onChange={(e) => setPayNetwork(e.target.value)}
                                  disabled={savingPlanId === "pro" || loadingUser}
                                >
                                  <option value="TMONEY">TMoney</option>
                                  <option value="FLOOZ">Flooz</option>
                                </select>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="vs-btn vs-btn-primary"
                              onClick={startProPayment}
                              disabled={savingPlanId === "pro" || loadingUser}
                            >
                              {savingPlanId === "pro"
                                ? "Redirection…"
                                : "Payer 15 000 FCFA avec PayGate"}
                            </button>

                            <p className="vs-plan-note">
                              Après paiement confirmé, votre Pack Pro s’active automatiquement.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <style>{`
          .vs-plans-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
          @media (max-width: 768px){ .vs-plans-grid { grid-template-columns:1fr; } }
          .vs-plan-card { background:#fff; border-radius:20px; border:1px solid #e5e7eb; padding:16px; box-shadow:0 12px 30px rgba(15,23,42,.06); display:flex; flex-direction:column; gap:8px; }
          .vs-plan-card-active { border-color:#22c55e; box-shadow:0 16px 40px rgba(34,197,94,.2); }
          .vs-plan-header { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
          .vs-plan-title { font-size:16px; font-weight:700; color:#111827; margin:0 0 2px; }
          .vs-plan-price { font-size:15px; font-weight:700; color:#111827; margin:0; }
          .vs-plan-subtitle { font-size:12px; color:#6b7280; margin:2px 0 0; }
          .vs-plan-badge { font-size:11px; font-weight:600; padding:4px 8px; border-radius:999px; background:#eff6ff; color:#1d4ed8; white-space:nowrap; }
          .vs-plan-description { font-size:13px; color:#4b5563; margin:4px 0; }
          .vs-plan-list { list-style:none; padding:0; margin:0 0 6px; display:grid; gap:4px; }
          .vs-plan-list li { display:flex; align-items:flex-start; gap:6px; font-size:13px; color:#111827; }
          .vs-plan-check { width:18px; height:18px; border-radius:999px; border:1px solid #d1d5db; display:inline-flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; }
          .vs-plan-footer { margin-top:4px; display:flex; flex-direction:column; gap:6px; }
          .vs-btn { display:inline-flex; align-items:center; justify-content:center; padding:8px 14px; border-radius:999px; font-size:13px; font-weight:600; border:1px solid transparent; cursor:pointer; }
          .vs-btn-primary { background:#1e17eb; color:#fff; border-color:#1e17eb; }
          .vs-btn-primary:hover:enabled { background:#111827; }
          .vs-btn-light { background:#fff; color:#111827; border-color:#d1d5db; }
          .vs-btn-light:hover:enabled { background:#f3f4f6; }
          .vs-btn-outline { background:#fff; color:#111827; border-color:#d1d5db; }
          .vs-btn-warning { color:#92400e; border-color:#facc15; background:#fef9c3; }
          .vs-btn:disabled { opacity:.7; cursor:default; }
          .vs-plan-note { font-size:11px; color:#6b7280; margin:0; }
        `}</style>
      </main>
    </div>
  );
}